/**
 * @ngdoc service
 * @name ccaSession
 * @description
 * This service works with the turin sessions, cerberus server data (via gateway)
 * and the browser storage in order to obtain and save auth details from the logged in user
 * and its permissions for the currently selected customer.
 *
 * @memberof common
 * @requires ngStorage
 */
class CCASession {
  /**
   * Class constructor.
   * @param {Service} $localStorage        To store the session information on the browser.
   * @param {Object}  appConstant          The app configuration dictionary.
   * @param {Service} contentCreatorAppAPI To set the access token.
   * @ignore
   */
  constructor(
    $localStorage,
    appConstant,
    contentCreatorAppAPI,
  ) {
    'ngInject';

    /**
     * The local reference to the `$localStorage` service.
     * @type {Object}
     */
    this.$localStorage = $localStorage;
    /**
     * The local reference to `contentCreatorAppAPI` service.
     * @type {Object}
     */
    this.contentCreatorAppAPI = contentCreatorAppAPI;
    /**
     * The local reference to the `activeSection` object.
     * @type {Object}
     */
    this.activeSection = {};

    const { version } = appConstant;
    /**
     * The key for the storage record that will save the currently active session information.
     * @type {String}
     */
    this.sessionKey = `CCA_SESSION_${version}`;
    /**
     * Once loaded, this property will have the currently active session information.
     * @type {Object|Null}
     */
    this.session = null;

    this._loadSession();
  }
  /**
   * Load a session from the storage into the service property.
   * @ignore
   */
  _loadSession() {
    this.session = this.$localStorage[this.sessionKey];

    if (this.isAuthenticated()) {
      this.contentCreatorAppAPI.setToken(this.session.token.value);
    }
    if (this.isAccountSelected()) {
      this.contentCreatorAppAPI
      .setCustomerId(this.getSelectedAccount().customer.id);
    }
  }
  /**
   * Update, create or remove any session information from the session storage. The key used is
   * the one on the `this.sessionKey` property.
   * @param {Object} data Optional. The data to add on the session storage. If the value of `data`
   *                      is _falsy_, it will remove the record from the storage.
   * @ignore
   */
  _writeSession(data = null) {
    if (data) {
      this.$localStorage[this.sessionKey] = Object.assign({}, data);
    } else {
      delete this.$localStorage[this.sessionKey];
    }
  }
  /**
   * Destroy the current session by clearing the storage and the service session information.
   */
  destroySession() {
    this._writeSession();
    this.session = null;
    this.contentCreatorAppAPI.clearToken();
    this.contentCreatorAppAPI.clearCustomerId();
  }
  /**
   * Return if the user has accepted the TOS.
   * @return {Boolean}
   */
  hasAcceptedTos() {
    const account = this.getSelectedAccount();
    return account
      ? account.customer.tos.accepted
      : false;
  }
  /**
   * Return if the user has already selected an account.
   * @return {Boolean}
   */
  isAccountSelected() {
    return this.session && this.session.selectedAccount !== -1;
  }
  /**
   * Return if the user has already authenticated.
   * @return {Boolean}
   */
  isAuthenticated() {
    return this.session && !!this.session.token;
  }
  /**
   * Gets the user accounts that has any permissions.
   * @return {Array}
   */
  getAccounts() {
    return this.getSession().accounts.filter((account) => account.permissions.length);
  }
  /**
   * Gets the selected account.
   * @return {?Object}
   */
  getSelectedAccount() {
    return this.isAccountSelected() ?
      this.session.accounts[this.session.selectedAccount]
      : null;
  }
  /**
   * Gets the current active session. If the service has no active session data, it will try to
   * load it from the storage.
   * @return {Object}
   */
  getSession() {
    if (!this.session) {
      this._loadSession();
    }

    return this.session;
  }
  /**
   * Gets the current user.
   * @return {Object}
   */
  getUser() {
    return this.getSession().user;
  }
  /**
   * Set an account as active.
   * @param  {Object} account the account to set as active.
   */
  setActiveAccount(account) {
    this.session.selectedAccount = this.session.accounts
    .findIndex((ac) => ac.customer.id === account.customer.id);
    this.storeSession(this.session);
  }
  /**
   * Set a section as active.
   * @param  {Object} activeSection the section to set as active.
   */
  setActiveSection(section) {
    this.activeSection = section;
  }
  /**
   * Mark the TOS as accepted by the current user.
   * @return {Promise}
   */
  setTosAccepted() {
    return this.contentCreatorAppAPI.setTosAccepted()
    .then(() => {
      const account = this.getSelectedAccount();
      account.customer.tos.accepted = true;
      this._writeSession(this.session);
    });
  }
  /**
   * Stores a session data.
   * @param  {Object} data
   */
  storeSession(data) {
    data.selectedAccount = angular.isDefined(data.selectedAccount)
      ? data.selectedAccount
      : -1;
    this._writeSession(data);
    this._loadSession();
  }
}

export default CCASession;



// WEBPACK FOOTER //
// ./src/frontend/modules/common/services/ccaSession.js